import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { map, catchError, forkJoin, of, timeout } from 'rxjs';
import { MessageService } from 'primeng/api';

import { FAMILY_PROFILE_FEATURE } from '../../features/family/components/family-profile/family-profile.schema';
import { FamilyDashboardService } from '../../features/family/services/family-dashboard.service';
import { hasRequiredValues } from '../../models/common/dynamic-form';
import { BaseSessionService } from '../services/base-session.service';
import { CoreService } from '../services/core.service';
import { DynamicFormService } from '../services/dynamic-form.service';

/** A hung profile or form-field call must not leave navigation pending forever. */
const PROFILE_CHECK_TIMEOUT_MS = 15_000;

/** Redirects to profile edit until required profile fields are filled. */
export const profileCompletionGuard: CanActivateFn = (_route, state) => {
  const session = inject(BaseSessionService);
  const familyService = inject(FamilyDashboardService);
  const dynamicForms = inject(DynamicFormService);
  const router = inject(Router);
  const messageService = inject(MessageService);

  const currentUrl = state.url;
  const isProfileRoute = currentUrl.includes('/family/profile');

  // Check if familyId exists in session (only set for family role)
  const familyId = session.getSessionItem(CoreService.KEY_FAMILY_ID);
  if (!familyId) {
    // Non-family role, skip profile completion check
    return of(true);
  }

  // Which fields are required is configuration, so the check reads the same
  // field list the profile form renders itself from.
  return forkJoin({
    profile: familyService.getProfile(),
    fields: dynamicForms.getFields(FAMILY_PROFILE_FEATURE)
  }).pipe(
    timeout(PROFILE_CHECK_TIMEOUT_MS),
    map(({ profile, fields }) => {
      const isProfileCompleted = hasRequiredValues(profile, fields);

      session.setProfileCompleted(isProfileCompleted);

      if (!isProfileCompleted) {
        if (isProfileRoute) {
          return true;
        }

        messageService.add({
          severity: 'info',
          summary: 'Complete Profile Required',
          detail: 'Please complete your profile before accessing other sections.',
          life: 3000
        });

        return router.createUrlTree(['/family/profile']);
      }

      return true;
    }),
    catchError((error) => {
      console.error('Profile completion guard error:', error);

      // Fall back to cached status when the profile fetch fails
      const isProfileCompleted = session.isProfileCompleted();

      if (!isProfileCompleted && !isProfileRoute) {
        messageService.add({
          severity: 'info',
          summary: 'Complete Profile Required',
          detail: 'Please complete your profile before accessing other sections.',
          life: 3000
        });

        return of(router.createUrlTree(['/family/profile']));
      }

      return of(true);
    })
  );
};
